import { AddRounded, DeleteRounded, DownloadRounded, DriveFolderUploadRounded, EditRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import AddViewImagesModal from "../AddViewImagesModal/AddViewImagesModal";
import { GlobalState } from "context/GlobalContext";
import { shootSkuImagesAssoc } from "../../CreateShoot/data";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { uploadImageFileTypes, uploadImageMimeTypes } from "data/globalData";

const fallbackChannel = "SHOPIFY";
const availableChannels = [
	{ label: "SHOPIFY", value: "SHOPIFY" },
	{ label: "AJIO", value: "AJIO" },
	{ label: "MYNTRA", value: "MYNTRA" },
];
const allowedChannelValues = new Set(availableChannels.map((channel) => channel.value));
const defaultViewImages = ["FRONT_VIEW", "SIDE_VIEW", "BACK_VIEW"];

const normalizeKey = (value = "") =>
	value
		.trim()
		.replace(/\s+/g, "_")
		.replace(/[^A-Za-z0-9_]/g, "")
		.toUpperCase();

const formatLabel = (value = "") => value.split("_").join(" ").toLowerCase();

const getImageChannel = (image) => image?.channel || fallbackChannel;
const acceptedImageExtensions = new Set(uploadImageFileTypes.map((type) => `.${type}`));

const isAllowedImageFile = (file) => {
	const fileName = file?.name?.toLowerCase?.() ?? "";
	const extension = fileName.includes(".") ? fileName.slice(fileName.lastIndexOf(".")) : "";
	return uploadImageMimeTypes.includes(file?.type) || acceptedImageExtensions.has(extension);
};

const getFileExtensionFromUrl = (url = "") => {
	const sanitizedUrl = url.split("?")[0];
	const match = sanitizedUrl.match(/\.([a-zA-Z0-9]+)$/);
	return match?.[1]?.toLowerCase() || "jpg";
};

const triggerDirectDownload = (url, filename) => {
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.rel = "noopener";
	anchor.target = "_blank";
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
};

const triggerBlobDownload = (blob, filename) => {
	const blobUrl = window.URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = blobUrl;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	window.URL.revokeObjectURL(blobUrl);
};

const getChannelMapForSku = (sku) => {
	const nextMap = {
		[fallbackChannel]: [...defaultViewImages],
	};

	(sku?.[shootSkuImagesAssoc] ?? []).forEach((image) => {
		const channel = getImageChannel(image);
		if (!nextMap[channel]) nextMap[channel] = [...defaultViewImages];
		if (image?.view && !nextMap[channel].includes(image.view)) nextMap[channel].push(image.view);
	});

	return nextMap;
};

const ImageBox = ({ data, handleDelete = () => {}, disableButtons = false }) => {
	return (
		<Box sx={{ position: "relative" }}>
			<img
				alt=""
				src={data?.url}
				style={{
					width: "100%",
					height: 180,
					objectFit: "cover",
					objectPosition: "50% 0%",
					borderRadius: 12,
					display: "block",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					right: 10,
					top: 10,
					display: disableButtons ? "none" : "block",
				}}>
				<IconButton
					disabled={disableButtons}
					onClick={() => handleDelete(data)}
					sx={{ background: "#11111199", "&:hover": { background: "#111111cc" } }}>
					<DeleteRounded sx={{ color: "#fff" }} />
				</IconButton>
			</Box>
		</Box>
	);
};

const ShootSkuImages = ({ shootSkus, setShootSkus, handleAddSkuClick = () => {}, hideAddSku = false, disableEdit = false }) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen } = GlobalState();

	const [addViewImgsModal, setAddViewImgsModal] = useState(false);
	const [structureEditModal, setStructureEditModal] = useState(false);
	const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);

	const [selectedSku, setSelectedSku] = useState();
	const [selectedChannel, setSelectedChannel] = useState(fallbackChannel);
	const [selectedImgModalView, setSelectedImgModalView] = useState();
	const [selectedImgModalChannel, setSelectedImgModalChannel] = useState(fallbackChannel);
	const [channelMap, setChannelMap] = useState({ [fallbackChannel]: [...defaultViewImages] });
	const [newChannelState, setNewChannelState] = useState("NYKA");
	const [newViewState, setNewViewState] = useState("");
	const [imgToDelState, setImgToDelState] = useState();
	const folderInputRef = useRef(null);

	useEffect(() => {
		if (!selectedSku) {
			if (shootSkus?.length) {
				setSelectedSku(shootSkus[0]);
			} else {
				setSelectedSku(null);
			}
			return;
		}

		const nextChannelMap = getChannelMapForSku(selectedSku);
		const channelKeys = Object.keys(nextChannelMap);
		setChannelMap(nextChannelMap);
		setSelectedChannel((prev) => (prev && nextChannelMap[prev] ? prev : channelKeys[0] || fallbackChannel));
	}, [selectedSku, shootSkus]);

	const selectedChannelViews = channelMap[selectedChannel] ?? defaultViewImages;
	const selectedSkuImages = useMemo(() => selectedSku?.[shootSkuImagesAssoc] ?? [], [selectedSku]);

	const channelSummary = useMemo(() => {
		return Object.keys(channelMap).map((channel) => {
			const imageCount = selectedSkuImages.filter((image) => getImageChannel(image) === channel).length;
			return { channel, imageCount };
		});
	}, [channelMap, selectedSkuImages]);

	const handleSkuSelect = (sku) => {
		setSelectedSku(shootSkus?.find((row) => row?.i_code === sku.i_code) ?? null);
	};

	const handleStructureModalClose = () => {
		setStructureEditModal(false);
		setNewChannelState("NYKA");
		setNewViewState("");
	};

	const handleDeleteConfirmModalClose = () => {
		setDeleteConfirmModal(false);
	};

	const handleAddImagesClick = (channel, view) => {
		setSelectedImgModalChannel(channel);
		setSelectedImgModalView(view);
		setAddViewImgsModal(true);
	};

	const addNewShootSkuImages = async (images, view, channel) => {
		const formData = new FormData();
		for (let i = 0; i < images.length; i++) {
			formData.append(shootSkuImagesAssoc, images[i].file);
		}
		formData.append("shoot_sku_id", String(selectedSku?.id ?? ""));
		formData.append("channel", channel);
		formData.append("view", view);

		const imageUrls = await axios
			.post("/api/studio/upload_shoot_sku_images", formData, {
				headers: {
					"content-type": "multipart/form-data",
				},
			})
			.then((res) => res.data.result);

		const productImages = imageUrls.map((row) => {
			return {
				i_code: row.i_code ?? selectedSku?.i_code,
				channel,
				view,
				is_main: false,
				url: row.publicUrl,
				file_path: row.filePath,
				shoot_sku_id: selectedSku?.id,
			};
		});

		try {
			const res = await axios.post("/api/studio/bulk_create_shoot_sku_images_sql", productImages);
			return res.data.result;
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
			return false;
		}
	};

	const handleSaveImagesClick = async (images) => {
		if (!images.length) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please select atleast one image", severity: "error" });
			return;
		}

		try {
			setLoadingOpen(true);
			const finalData = await addNewShootSkuImages(images, selectedImgModalView, selectedImgModalChannel);
			if (!finalData) return;

			setSelectedSku((prev) => {
				return {
					...prev,
					[shootSkuImagesAssoc]: [...(prev?.[shootSkuImagesAssoc] ?? []), ...finalData],
				};
			});

			setShootSkus((prev) => {
				return prev.map((sku) => {
					if (sku?.i_code !== selectedSku?.i_code) return sku;

					return {
						...sku,
						[shootSkuImagesAssoc]: [...(sku?.[shootSkuImagesAssoc] ?? []), ...finalData],
					};
				});
			});

			setChannelMap((prev) => ({
				...prev,
				[selectedImgModalChannel]: prev[selectedImgModalChannel]?.includes(selectedImgModalView)
					? prev[selectedImgModalChannel]
					: [...(prev[selectedImgModalChannel] ?? defaultViewImages), selectedImgModalView],
			}));

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Images added successfully",
				severity: "success",
			});
			setAddViewImgsModal(false);
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
			setLoadingOpen(false);
		}
	};

	const handleBulkFolderButtonClick = () => {
		folderInputRef.current?.click();
	};

	const handleBulkFolderUpload = async (event) => {
		const rawFiles = Array.from(event.target.files ?? []);
		event.target.value = null;

		if (!selectedSku) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please select a SKU first", severity: "error" });
			return;
		}

		const validFiles = rawFiles.filter(isAllowedImageFile);
		if (!validFiles.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `No supported images found. Allowed types: ${uploadImageFileTypes.join(", ")}`,
				severity: "error",
			});
			return;
		}

		const parsedEntries = [];
		let invalidChannelCount = 0;
		for (const file of validFiles) {
			const pathParts = (file.webkitRelativePath || file.name).split("/").filter(Boolean);
			const relativeParts = pathParts.length > 1 ? pathParts.slice(1) : pathParts;

			let channel = selectedChannel || fallbackChannel;
			let view = "";

			if (relativeParts.length >= 3) {
				channel = normalizeKey(relativeParts[0]);
				if (!allowedChannelValues.has(channel)) {
					invalidChannelCount += 1;
					continue;
				}
				view = normalizeKey(relativeParts[1]);
			} else if (relativeParts.length >= 2) {
				view = normalizeKey(relativeParts[0]);
			}

			if (!view) continue;

			parsedEntries.push({
				file,
				channel,
				view,
			});
		}

		if (!parsedEntries.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Folder format should be channel/view/image or view/image inside the selected channel",
				severity: "error",
			});
			return;
		}

		if (invalidChannelCount > 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `${invalidChannelCount} files were skipped because their channel folder is not one of D2C, NYKA, MYNTRA`,
				severity: "warning",
			});
		}

		try {
			setLoadingOpen(true);
			const formData = new FormData();
			parsedEntries.forEach((entry) => {
				formData.append(shootSkuImagesAssoc, entry.file);
			});
			formData.append(
				"uploadMeta",
				JSON.stringify(
					parsedEntries.map((entry) => ({
						fileName: entry.file.name,
						shoot_sku_id: selectedSku?.id,
						channel: entry.channel,
						view: entry.view,
					})),
				),
			);

			const imageUrls = await axios
				.post("/api/studio/upload_shoot_sku_images", formData, {
					headers: {
						"content-type": "multipart/form-data",
					},
				})
				.then((res) => res.data.result);

			const sqlPayload = imageUrls.map((row, index) => ({
				i_code: row.i_code ?? selectedSku?.i_code,
				channel: row.channel ?? parsedEntries[index].channel,
				view: row.view ?? parsedEntries[index].view,
				is_main: false,
				url: row.publicUrl,
				file_path: row.filePath,
				shoot_sku_id: selectedSku?.id,
			}));

			const finalData = await axios
				.post("/api/studio/bulk_create_shoot_sku_images_sql", sqlPayload)
				.then((res) => res.data.result);

			const nextChannelMap = { ...channelMap };
			parsedEntries.forEach(({ channel, view }) => {
				if (!nextChannelMap[channel]) nextChannelMap[channel] = [...defaultViewImages];
				if (!nextChannelMap[channel].includes(view)) nextChannelMap[channel].push(view);
			});

			setChannelMap(nextChannelMap);
			setSelectedSku((prev) => ({
				...prev,
				[shootSkuImagesAssoc]: [...(prev?.[shootSkuImagesAssoc] ?? []), ...finalData],
			}));
			setShootSkus((prev) =>
				prev.map((sku) => {
					if (sku?.i_code !== selectedSku?.i_code) return sku;
					return {
						...sku,
						[shootSkuImagesAssoc]: [...(sku?.[shootSkuImagesAssoc] ?? []), ...finalData],
					};
				}),
			);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `${finalData.length} images uploaded successfully`,
				severity: "success",
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
			});
			setLoadingOpen(false);
		}
	};

	const handleSkuDownload = async () => {
		if (!selectedSkuImages.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No images available for download",
				severity: "warning",
			});
			return;
		}

		try {
			setLoadingOpen(true);
			for (let index = 0; index < selectedSkuImages.length; index += 1) {
				const image = selectedSkuImages[index];
				const channel = getImageChannel(image);
				const view = image?.view || "UNMAPPED_VIEW";
				const extension = getFileExtensionFromUrl(image?.url);
				const fileName = `${selectedSku?.i_code || "sku"}_${channel}_${view}_${index + 1}.${extension}`;

				try {
					const response = await fetch(image?.url, { mode: "cors" });
					if (!response.ok) throw new Error("Blob download failed");
					const blob = await response.blob();
					triggerBlobDownload(blob, fileName);
				} catch (downloadError) {
					triggerDirectDownload(image?.url, fileName);
				}

				await new Promise((resolve) => setTimeout(resolve, 120));
			}

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Started SKU image downloads. Browser may save them individually instead of in folders.",
				severity: "success",
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
			});
			setLoadingOpen(false);
		}
	};

	const handleChannelDelete = (channel) => {
		if (channel === fallbackChannel) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Default channel cannot be removed",
				severity: "error",
			});
			return;
		}

		if (selectedSkuImages.some((row) => getImageChannel(row) === channel)) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please remove images from this channel before removing it",
				severity: "error",
			});
			return;
		}

		setChannelMap((prev) => {
			const next = { ...prev };
			delete next[channel];
			return next;
		});
		if (selectedChannel === channel) setSelectedChannel(fallbackChannel);
	};

	const handleViewDelete = (channel, view) => {
		if (defaultViewImages.includes(view)) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Default views cannot be removed",
				severity: "error",
			});
			return;
		}

		if (selectedSkuImages.some((row) => row?.view === view && getImageChannel(row) === channel)) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please remove images from this view before removing it",
				severity: "error",
			});
			return;
		}

		setChannelMap((prev) => ({
			...prev,
			[channel]: prev[channel]?.filter((existingView) => existingView !== view) ?? [],
		}));
	};

	const handleSkuImageDeleteClick = (image) => {
		setImgToDelState(image);
		setDeleteConfirmModal(true);
	};

	const handleCancelSkuImageDeleteClick = () => {
		setImgToDelState();
		handleDeleteConfirmModalClose();
	};

	const handleSkuImageDelete = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);

		try {
			await axios.delete(`/api/studio/delete_shoot_sku_image/${imgToDelState.id}`);

			setSelectedSku((prev) => ({
				...prev,
				[shootSkuImagesAssoc]: prev?.[shootSkuImagesAssoc]?.filter((row) => row?.id !== imgToDelState.id),
			}));

			setShootSkus((prev) =>
				prev.map((sku) => {
					if (sku?.i_code !== selectedSku?.i_code) return sku;
					return {
						...sku,
						[shootSkuImagesAssoc]: sku?.[shootSkuImagesAssoc]?.filter((row) => row?.id !== imgToDelState.id),
					};
				}),
			);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Image deleted successfully",
				severity: "success",
			});
			handleDeleteConfirmModalClose();
			setImgToDelState();
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
			setLoadingOpen(false);
		}
	};

	return (
		<Box width="100%" maxWidth="100%" overflow="hidden" display="flex" flexWrap="wrap">
			<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" pt={1} pb={2}>
				<Box>
					<Typography variant="h5">SKU Image Library</Typography>
					<Typography variant="body2" color="text.secondary">
						Organize images by channel first, then by view.
					</Typography>
				</Box>
				<Button
					variant="contained"
					size="small"
					onClick={handleAddSkuClick}
					sx={{ display: hideAddSku ? "none" : "block" }}
					disabled={disableEdit}>
					Add SKU
				</Button>
			</Box>
			<input
				ref={folderInputRef}
				type="file"
				hidden
				multiple
				accept={uploadImageFileTypes.map((type) => `.${type}`).join(",")}
				onChange={handleBulkFolderUpload}
				webkitdirectory=""
				directory=""
			/>

			<Box display="flex" width="100%" minHeight="60vh" gap={2} flexWrap="wrap" overflow="hidden" maxHeight="85vh">
				<Paper
					elevation={0}
					sx={{
						width: { xs: "100%", lg: 280 },
						flexShrink: 0,
						maxWidth: "100%",
						border: `1px solid ${grey[200]}`,
						borderRadius: 3,
						p: 1.5,
						background: grey[50],
						height: "100%",
						overflowY: "auto",
					}}>
					<Typography variant="overline" color="text.secondary">
						Skus
					</Typography>
					<List sx={{ pt: 0.5 }}>
						{shootSkus?.map((row) => (
							<ListItem key={row?.i_code} disablePadding sx={{ mb: 0.75 }}>
								<ListItemButton
									onClick={() => handleSkuSelect(row)}
									selected={selectedSku?.i_code === row?.i_code}
									sx={{
										borderRadius: 2,
										border: `1px solid ${selectedSku?.i_code === row?.i_code ? grey[900] : grey[200]}`,
										background: selectedSku?.i_code === row?.i_code ? grey[100] : "transparent",
									}}>
									<ListItemText
										primary={row?.i_code}
										secondary={`${row?.[shootSkuImagesAssoc]?.length ?? 0} images`}
										primaryTypographyProps={{ fontWeight: 600 }}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</Paper>

				<Box flex={1} minWidth={0} maxWidth="100%" maxHeight="100%" overflow="hidden" sx={{ overflowY: "auto" }}>
					<Paper
						elevation={0}
						sx={{
							border: `1px solid ${grey[200]}`,
							borderRadius: 3,
							p: 2,
							minHeight: "100%",
							background:
								"linear-gradient(180deg, rgba(245,245,245,0.55) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,1) 100%)",
							boxSizing: "border-box",
						}}>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="flex-start"
							gap={2}
							flexWrap={{ xs: "wrap", lg: "nowrap" }}
							mb={2}>
							<Box minWidth={0} flex={1}>
								<Typography variant="h6">
									{selectedSku?.i_code ? `${selectedSku.i_code} channels` : "Channels"}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Each channel contains its own view set and image collection.
								</Typography>
								<Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
									Bulk folder format: `channel/view/image.jpg` or `view/image.jpg` inside the selected channel.
								</Typography>
							</Box>
							<Stack
								direction="row"
								spacing={1}
								flexWrap={{ xs: "wrap", sm: "nowrap" }}
								justifyContent="flex-end"
								alignItems="flex-start"
								sx={{ ml: "auto" }}>
								<Button
									variant="outlined"
									startIcon={<DownloadRounded />}
									onClick={handleSkuDownload}
									disabled={!selectedSku || !selectedSkuImages.length}>
									Download SKU
								</Button>
								<Button
									variant="outlined"
									startIcon={<DriveFolderUploadRounded />}
									onClick={handleBulkFolderButtonClick}
									disabled={disableEdit || !selectedSku}>
									Bulk Upload Folder
								</Button>
							</Stack>
						</Box>

						<Box display="flex" flexWrap="wrap" gap={1} mb={3}>
							{channelSummary.map(({ channel, imageCount }) => (
								<Chip
									key={channel}
									label={`${formatLabel(channel)} (${imageCount})`}
									onClick={() => setSelectedChannel(channel)}
									variant={selectedChannel === channel ? "filled" : "outlined"}
									sx={{
										height: 36,
										textTransform: "capitalize",
										backgroundColor: selectedChannel === channel ? grey[900] : "transparent",
										color: selectedChannel === channel ? "#fff" : grey[900],
										borderColor: grey[400],
									}}
								/>
							))}
							<IconButton
								size="small"
								onClick={() => setStructureEditModal(true)}
								disabled={disableEdit || !selectedSku}
								sx={{
									border: `1px solid ${grey[300]}`,
									borderRadius: 2,
									width: 36,
									height: 36,
								}}>
								<EditRounded fontSize="small" />
							</IconButton>
						</Box>

						<Box
							display="grid"
							gridTemplateColumns="repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
							gap={2}
							alignItems="stretch"
							maxWidth="100%">
							{selectedSku &&
								selectedChannelViews.map((view) => {
									const images = selectedSkuImages.filter(
										(row) => row?.view === view && getImageChannel(row) === selectedChannel,
									);

									return (
										<Paper
											key={`${selectedChannel}-${view}`}
											elevation={0}
											sx={{
												border: `1px solid ${grey[200]}`,
												borderRadius: 3,
												p: 1.5,
												background: "#fff",
												minWidth: 0,
												overflow: "hidden",
											}}>
											<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
												<Box>
													<Typography variant="subtitle1" fontWeight={700} textTransform="capitalize">
														{formatLabel(view)}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{images.length} image{images.length === 1 ? "" : "s"} in{" "}
														{formatLabel(selectedChannel)}
													</Typography>
												</Box>
												<Button
													variant="contained"
													size="small"
													startIcon={<AddRounded />}
													onClick={() => handleAddImagesClick(selectedChannel, view)}
													disabled={disableEdit}>
													Add Images
												</Button>
											</Box>

											{images.length ? (
												<Box
													display="grid"
													gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
													gap={1.25}
													maxWidth="100%">
													{images.map((row) => (
														<ImageBox
															key={row?.id ?? `${row?.url}-${row?.view}-${getImageChannel(row)}`}
															data={row}
															disableButtons={disableEdit}
															handleDelete={handleSkuImageDeleteClick}
														/>
													))}
												</Box>
											) : (
												<Box
													height={180}
													border={`1px dashed ${grey[300]}`}
													borderRadius={3}
													display="flex"
													alignItems="center"
													justifyContent="center"
													color={grey[600]}>
													<Typography variant="body2">No images yet for this channel/view</Typography>
												</Box>
											)}
										</Paper>
									);
								})}
						</Box>
					</Paper>
				</Box>
			</Box>

			{addViewImgsModal && (
				<AddViewImagesModal
					modalOpen={addViewImgsModal}
					setModalOpen={setAddViewImgsModal}
					submitFunction={handleSaveImagesClick}
					view={selectedImgModalView}
					channel={selectedImgModalChannel}
				/>
			)}

			<Dialog
				fullWidth
				maxWidth="xs"
				open={deleteConfirmModal}
				onClose={handleDeleteConfirmModalClose}
				component="form"
				keepMounted={false}
				onSubmit={handleSkuImageDelete}>
				<Typography p={2} boxSizing="border-box" variant="h6">
					Are you sure you want to delete this image?
				</Typography>
				<Box px={2} width="100%" boxSizing="border-box" mb={2} display="flex" justifyContent="center">
					<Box width={220}>
						<ImageBox data={imgToDelState} disableButtons={true} />
					</Box>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleCancelSkuImageDeleteClick}>
						Cancel
					</Button>
					<Button autoFocus variant="contained" sx={{ mr: 1 }} type="submit">
						Confirm Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog fullWidth maxWidth="md" open={structureEditModal} onClose={handleStructureModalClose}>
				<Typography width="100%" p={2} pb={1} boxSizing="border-box" variant="h6">
					Manage Channels & Views
				</Typography>
				<Box px={2} pb={2} sx={{ overflowX: "hidden" }}>
					<Typography variant="body2" color="text.secondary" mb={2}>
						Channels group your SKU imagery. Views live inside each channel.
					</Typography>

					<Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3} alignItems={{ xs: "stretch", md: "flex-start" }}>
						<TextField
							select
							label="Add Channel"
							size="small"
							fullWidth
							value={newChannelState}
							onChange={(e) => setNewChannelState(e.target.value)}>
							{availableChannels.map((channel) => (
								<MenuItem key={channel.value} value={channel.value}>
									{channel.label}
								</MenuItem>
							))}
						</TextField>
						<Button
							variant="contained"
							sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0, minHeight: 40 }}
							onClick={() => {
								const normalizedChannel = normalizeKey(newChannelState);
								if (channelMap[normalizedChannel]) {
									setSnackBarOpen(true);
									setSnackBar({
										...snackBar,
										message: "Channel already exists",
										severity: "error",
									});
									return;
								}
								setChannelMap((prev) => ({
									...prev,
									[normalizedChannel]: [...defaultViewImages],
								}));
								setSelectedChannel(normalizedChannel);
								setNewChannelState("NYKA");
							}}>
							Add Channel
						</Button>
					</Stack>

					<Box display="flex" flexWrap="wrap" gap={1} mb={3} maxWidth="100%">
						{Object.keys(channelMap).map((channel) => (
							<Chip
								key={channel}
								label={formatLabel(channel)}
								onClick={() => setSelectedChannel(channel)}
								onDelete={disableEdit ? undefined : () => handleChannelDelete(channel)}
								disabled={channel === fallbackChannel}
								variant={selectedChannel === channel ? "filled" : "outlined"}
								sx={{ textTransform: "capitalize" }}
							/>
						))}
					</Box>

					<Divider sx={{ mb: 2 }} />

					<Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }} mb={2}>
						<TextField
							label={`Add View to ${formatLabel(selectedChannel)}`}
							size="small"
							fullWidth
							value={newViewState}
							onChange={(e) => setNewViewState(e.target.value)}
						/>
						<Button
							variant="contained"
							sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0, minHeight: 40 }}
							onClick={() => {
								const normalizedView = normalizeKey(newViewState);
								if (!normalizedView) {
									setSnackBarOpen(true);
									setSnackBar({
										...snackBar,
										message: "Please enter a valid view name",
										severity: "error",
									});
									return;
								}
								if ((channelMap[selectedChannel] ?? []).includes(normalizedView)) {
									setSnackBarOpen(true);
									setSnackBar({
										...snackBar,
										message: "View already exists in this channel",
										severity: "error",
									});
									return;
								}
								setChannelMap((prev) => ({
									...prev,
									[selectedChannel]: [...(prev[selectedChannel] ?? []), normalizedView],
								}));
								setNewViewState("");
							}}>
							Add View
						</Button>
					</Stack>

					<Box display="flex" flexWrap="wrap" gap={1} maxWidth="100%">
						{(channelMap[selectedChannel] ?? []).map((view) => (
							<Chip
								key={`${selectedChannel}-${view}`}
								label={formatLabel(view)}
								onDelete={disableEdit ? undefined : () => handleViewDelete(selectedChannel, view)}
								disabled={defaultViewImages.includes(view)}
								sx={{ textTransform: "capitalize" }}
							/>
						))}
					</Box>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleStructureModalClose}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ShootSkuImages;
